/**
 * Web implementation of the subset of the React Native API used by this app.
 *
 * Every component renders a real DOM element. RN style objects are converted to
 * CSS via `styleUtils`. This module is aliased to `react-native` (and
 * `react-native-gesture-handler`) in `vite.config.ts` / `tsconfig.web.json`.
 */
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toCss, viewBase, flattenStyle, filterDomProps } from './styleUtils';
import type { RNStyle } from './styleUtils';

export type StyleProp<T = RNStyle> = T | false | null | undefined | Array<StyleProp<T>>;

/* -------------------------------------------------------------------------- */
/* View                                                                       */
/* -------------------------------------------------------------------------- */

export interface ViewProps extends React.HTMLAttributes<HTMLDivElement> {
  style?: StyleProp;
  pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only';
  testID?: string;
  onLayout?: (e: { nativeEvent: { layout: { width: number; height: number; x: number; y: number } } }) => void;
}

export const View = forwardRef<HTMLDivElement, ViewProps>(function View(
  { style, pointerEvents, testID, onLayout, children, ...rest },
  ref,
) {
  const localRef = useRef<HTMLDivElement | null>(null);
  const setRef = (node: HTMLDivElement | null) => {
    localRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  };

  useEffect(() => {
    if (onLayout && localRef.current) {
      const el = localRef.current;
      const report = () =>
        onLayout({
          nativeEvent: {
            layout: { width: el.offsetWidth, height: el.offsetHeight, x: el.offsetLeft, y: el.offsetTop },
          },
        });
      report();
      const ro = new ResizeObserver(report);
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, [onLayout]);

  const css = { ...viewBase, ...toCss(style) };
  if (pointerEvents) css.pointerEvents = pointerEvents === 'box-none' ? 'none' : (pointerEvents as any);

  return (
    <div ref={setRef} data-testid={testID} style={css} {...filterDomProps(rest)}>
      {children}
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/* Text                                                                        */
/* -------------------------------------------------------------------------- */

const TextAncestorContext = createContext(false);

export interface TextProps extends React.HTMLAttributes<HTMLSpanElement> {
  style?: StyleProp;
  numberOfLines?: number;
  onPress?: () => void;
  ellipsizeMode?: string;
  selectable?: boolean;
}

export const Text = forwardRef<HTMLSpanElement, TextProps>(function Text(
  { style, numberOfLines, onPress, ellipsizeMode, selectable, children, ...rest },
  ref,
) {
  const isNested = useContext(TextAncestorContext);
  const css: RNStyle = {
    display: isNested ? 'inline' : 'block',
    margin: 0,
    padding: 0,
    ...toCss(style),
  };
  if (!isNested) {
    css.whiteSpace = 'pre-wrap';
    css.wordBreak = 'break-word';
  }
  if (onPress) css.cursor = 'pointer';
  if (numberOfLines === 1) {
    css.whiteSpace = 'nowrap';
    css.overflow = 'hidden';
    css.textOverflow = 'ellipsis';
  } else if (numberOfLines && numberOfLines > 1) {
    css.display = '-webkit-box';
    (css as any).WebkitLineClamp = numberOfLines;
    (css as any).WebkitBoxOrient = 'vertical';
    css.overflow = 'hidden';
  }

  return (
    <TextAncestorContext.Provider value={true}>
      <span ref={ref}  style={css} onClick={onPress} {...filterDomProps(rest)}>
        {children}
      </span>
    </TextAncestorContext.Provider>
  );
});

/* -------------------------------------------------------------------------- */
/* ScrollView                                                                  */
/* -------------------------------------------------------------------------- */

export interface ScrollViewProps extends ViewProps {
  horizontal?: boolean;
  contentContainerStyle?: StyleProp;
  showsVerticalScrollIndicator?: boolean;
  showsHorizontalScrollIndicator?: boolean;
  keyboardShouldPersistTaps?: string;
  refreshControl?: React.ReactNode;
  scrollEnabled?: boolean;
  onScroll?: (e: any) => void;
  stickyHeaderIndices?: number[];
}

export const ScrollView = forwardRef<HTMLDivElement, ScrollViewProps>(function ScrollView(
  { horizontal, contentContainerStyle, style, children, scrollEnabled = true, onScroll, refreshControl, ...rest },
  ref,
) {
  const outer: RNStyle = {
    ...viewBase,
    flex: (flattenStyle(style) as any).flex ?? 1,
    overflowX: horizontal ? (scrollEnabled ? 'auto' : 'hidden') : 'hidden',
    overflowY: horizontal ? 'hidden' : scrollEnabled ? 'auto' : 'hidden',
    WebkitOverflowScrolling: 'touch',
    ...toCss(style),
  };
  const inner: RNStyle = {
    display: 'flex',
    flexDirection: horizontal ? 'row' : 'column',
    ...(horizontal ? {} : { minHeight: 'min-content', flex: 1}),
    ...toCss(contentContainerStyle),
  };
  const handleScroll = onScroll
    ? (e: React.UIEvent<HTMLDivElement>) => {
        const t = e.currentTarget;
        onScroll({
          nativeEvent: {
            contentOffset: { x: t.scrollLeft, y: t.scrollTop },
            contentSize: { width: t.scrollWidth, height: t.scrollHeight },
            layoutMeasurement: { width: t.clientWidth, height: t.clientHeight },
          },
        });
      }
    : undefined;

  return (
    <div ref={ref} style={outer} onScroll={handleScroll} {...filterDomProps(rest)}>
      {refreshControl}
      <div style={inner}>{children}</div>
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/* Touchables / Pressable                                                      */
/* -------------------------------------------------------------------------- */

interface TouchableProps extends ViewProps {
  onPress?: (e?: any) => void;
  onLongPress?: () => void;
  disabled?: boolean;
  activeOpacity?: number;
  hitSlop?: any;
  delayLongPress?: number;
}

function usePressable(disabled?: boolean, onPress?: (e?: any) => void, onLongPress?: () => void, delay = 500) {
  const [pressed, setPressed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPointerDown = () => {
    if (disabled) return;
    setPressed(true);
    if (onLongPress) {
      timer.current = setTimeout(() => onLongPress(), delay);
    }
  };
  const clear = () => {
    setPressed(false);
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  const onClick = (e: React.MouseEvent) => {
    if (disabled) return;
    onPress?.(e);
  };
  return { pressed, handlers: { onPointerDown, onPointerUp: clear, onPointerLeave: clear, onClick } };
}

export const TouchableOpacity = forwardRef<HTMLDivElement, TouchableProps>(function TouchableOpacity(
  { onPress, onLongPress, disabled, activeOpacity = 0.2, style, children, delayLongPress, ...rest },
  ref,
) {
  const { pressed, handlers } = usePressable(disabled, onPress, onLongPress, delayLongPress);
  const css: RNStyle = {
    ...viewBase,
    cursor: disabled ? 'default' : 'pointer',
    opacity: pressed ? activeOpacity : disabled ? 0.6 : 1,
    transition: 'opacity 0.1s ease',
    userSelect: 'none',
    ...toCss(style),
  };
  return (
    <div ref={ref} style={css} {...handlers} {...filterDomProps(rest)}>
      {children}
    </div>
  );
});

export const TouchableHighlight = TouchableOpacity;

export const TouchableWithoutFeedback = function TouchableWithoutFeedback({
  onPress,
  disabled,
  children,
}: TouchableProps) {
  const child = React.Children.only(children) as React.ReactElement;
  return React.cloneElement(child, {
    onClick: (e: React.MouseEvent) => {
      if (!disabled) onPress?.(e);
      (child.props as any).onClick?.(e);
    },
  });
};

export interface PressableProps extends Omit<ViewProps, 'style'> {
  onPress?: (e?: any) => void;
  onLongPress?: () => void;
  disabled?: boolean;
  hitSlop?: any;
  style?: StyleProp | ((state: PressableStateCallbackType) => StyleProp);
  children?: React.ReactNode | ((state: PressableStateCallbackType) => React.ReactNode);
}

export interface PressableStateCallbackType {
  pressed: boolean;
}

export const Pressable = forwardRef<HTMLDivElement, PressableProps>(function Pressable(
  { onPress, onLongPress, disabled, style, children, ...rest },
  ref,
) {
  const { pressed, handlers } = usePressable(disabled, onPress, onLongPress);
  const resolvedStyle = typeof style === 'function' ? style({ pressed }) : style;
  const css: RNStyle = {
    ...viewBase,
    cursor: disabled ? 'default' : 'pointer',
    userSelect: 'none',
    ...toCss(resolvedStyle as StyleProp),
  };
  const content = typeof children === 'function' ? children({ pressed }) : children;
  return (
    <div ref={ref} style={css} {...handlers} {...filterDomProps(rest)}>
      {content}
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/* TextInput                                                                   */
/* -------------------------------------------------------------------------- */

const keyboardTypeToInputMode: Record<string, string> = {
  'numeric': 'numeric',
  'number-pad': 'numeric',
  'decimal-pad': 'decimal',
  'phone-pad': 'tel',
  'email-address': 'email',
  'url': 'url',
  'default': 'text',
};

export interface TextInputProps {
  value?: string;
  defaultValue?: string;
  onChangeText?: (text: string) => void;
  onChange?: (e: any) => void;
  placeholder?: string;
  placeholderTextColor?: string;
  secureTextEntry?: boolean;
  keyboardType?: string;
  editable?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  autoFocus?: boolean;
  autoCapitalize?: string;
  autoCorrect?: boolean;
  maxLength?: number;
  style?: StyleProp;
  onFocus?: (e?: any) => void;
  onBlur?: (e?: any) => void;
  onSubmitEditing?: () => void;
  testID?: string;
  returnKeyType?: string;
  selectTextOnFocus?: boolean;
  [key: string]: any;
}

export const TextInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextInputProps>(
  function TextInput(
    {
      value,
      defaultValue,
      onChangeText,
      onChange,
      placeholder,
      secureTextEntry,
      keyboardType = 'default',
      editable = true,
      multiline,
      numberOfLines,
      autoFocus,
      autoCapitalize,
      autoCorrect,
      maxLength,
      style,
      onFocus,
      onBlur,
      onSubmitEditing,
      testID,
      placeholderTextColor,
      ...rest
    },
    ref,
  ) {
    const css: RNStyle = {
      border: 'none',
      outline: 'none',
      background: 'transparent',
      font: 'inherit',
      color: 'inherit',
      padding: 0,
      margin: 0,
      boxSizing: 'border-box',
      resize: 'none',
      ...toCss(style),
    };
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeText?.(e.target.value);
      onChange?.({ nativeEvent: { text: e.target.value } });
    };
    const commonProps: any = {
      value,
      defaultValue,
      placeholder,
      disabled: !editable,
      readOnly: !editable,
      autoFocus,
      maxLength,
      style: css,
      onChange: handleChange,
      onFocus,
      onBlur,
      'data-testid': testID,
      autoCapitalize,
      autoCorrect: autoCorrect === false ? 'off' : 'on',
      ...rest,
    };
    if (multiline) {
      return <textarea ref={ref as any} rows={numberOfLines} {...commonProps} />;
    }
    return (
      <input
        ref={ref as any}
        type={secureTextEntry ? 'password' : 'text'}
        inputMode={keyboardTypeToInputMode[keyboardType] as any}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmitEditing?.();
        }}
        {...commonProps}
      />
    );
  },
);

/* -------------------------------------------------------------------------- */
/* Image                                                                       */
/* -------------------------------------------------------------------------- */

export interface ImageProps {
  source?: { uri?: string } | string | number;
  style?: StyleProp;
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'center' | 'repeat';
  testID?: string;
  accessibilityLabel?: string;
  [key: string]: any;
}

export const Image = function Image({ source, style, resizeMode = 'cover', testID, ...rest }: ImageProps) {
  const uri = typeof source === 'string' ? source : (source as any)?.uri ?? '';
  const objectFit = resizeMode === 'stretch' ? 'fill' : resizeMode === 'center' ? 'none' : resizeMode;
  return <img src={uri} data-testid={testID} style={{ ...toCss(style), objectFit } as any} {...rest} />;
};

export const ImageBackground = function ImageBackground({
  source,
  style,
  imageStyle,
  resizeMode = 'cover',
  children,
  ...rest
}: ImageProps & { imageStyle?: StyleProp; children?: React.ReactNode }) {
  const uri = typeof source === 'string' ? source : (source as any)?.uri ?? '';
  return (
    <div
      style={{
        ...viewBase,
        backgroundImage: uri ? `url(${uri})` : undefined,
        backgroundSize: resizeMode === 'stretch' ? '100% 100%' : resizeMode === 'center' ? 'auto' : resizeMode,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        ...(toCss(style) as any),
        ...(toCss(imageStyle) as any),
      }}
      {...rest}
    >
      {children}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* ActivityIndicator                                                           */
/* -------------------------------------------------------------------------- */

export const ActivityIndicator = function ActivityIndicator({
  size = 'small',
  color = '#999',
  style,
}: {
  size?: 'small' | 'large' | number;
  color?: string;
  style?: StyleProp;
}) {
  const dimension = typeof size === 'number' ? size : size === 'large' ? 36 : 20;
  return (
    <div style={{ ...toCss(style), display: 'inline-block' } as any}>
      <div
        style={{
          width: dimension,
          height: dimension,
          border: `${Math.max(2, dimension / 10)}px solid ${color}`,
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'rn-spin 0.8s linear infinite',
        }}
      />
      <style>{'@keyframes rn-spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Switch                                                                       */
/* -------------------------------------------------------------------------- */

export const Switch = function Switch({
  value,
  onValueChange,
  disabled,
  trackColor,
  thumbColor,
}: {
  value?: boolean;
  onValueChange?: (v: boolean) => void;
  disabled?: boolean;
  trackColor?: { false?: string; true?: string };
  thumbColor?: string;
}) {
  return (
    <label style={{ display: 'inline-flex', width: 51, height: 31, position: 'relative', cursor: disabled ? 'default' : 'pointer' }}>
      <input
        type="checkbox"
        checked={!!value}
        disabled={disabled}
        onChange={(e) => onValueChange?.(e.target.checked)}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 31,
          transition: '0.2s',
          background: value ? trackColor?.true ?? '#00A3E0' : trackColor?.false ?? '#ccc',
        }}
      />
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: value ? 22 : 2,
          width: 27,
          height: 27,
          borderRadius: '50%',
          background: thumbColor ?? '#fff',
          transition: '0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}
      />
    </label>
  );
};

/* -------------------------------------------------------------------------- */
/* SafeAreaView / KeyboardAvoidingView                                          */
/* -------------------------------------------------------------------------- */

export const SafeAreaView = forwardRef<HTMLDivElement, ViewProps>(function SafeAreaView(
  { style, children, ...rest },
  ref,
) {
  const css: RNStyle = {
    ...viewBase,
    // paddingTop: 'env(safe-area-inset-top)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    ...toCss(style),
  };
  return (
    <div ref={ref} style={css} {...filterDomProps(rest)}>
      {children}
    </div>
  );
});

export const KeyboardAvoidingView = View;

/* -------------------------------------------------------------------------- */
/* FlatList / SectionList                                                       */
/* -------------------------------------------------------------------------- */

export interface FlatListProps<T = any> extends Omit<ScrollViewProps, 'children'> {
  data?: readonly T[] | null;
  renderItem?: (info: { item: T; index: number }) => React.ReactNode;
  keyExtractor?: (item: T, index: number) => string;
  ListHeaderComponent?: React.ReactNode | React.ComponentType;
  ListFooterComponent?: React.ReactNode | React.ComponentType;
  ListEmptyComponent?: React.ReactNode | React.ComponentType;
  ItemSeparatorComponent?: React.ComponentType;
  numColumns?: number;
  onEndReached?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  extraData?: any;
}

function renderElement(node: React.ReactNode | React.ComponentType): React.ReactNode {
  if (!node) return null;
  if (typeof node === 'function') {
    const C = node as React.ComponentType;
    return <C />;
  }
  return node as React.ReactNode;
}

export function FlatList<T = any>({
  data,
  renderItem,
  keyExtractor,
  ListHeaderComponent,
  ListFooterComponent,
  ListEmptyComponent,
  ItemSeparatorComponent,
  horizontal,
  contentContainerStyle,
  style,
  onEndReached,
  ...rest
}: FlatListProps<T>) {
  const items = data ?? [];
  const handleScroll = onEndReached
    ? (e: any) => {
        const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
        if (horizontal) {
          if (contentOffset.x + layoutMeasurement.width >= contentSize.width - 40) onEndReached();
        } else if (contentOffset.y + layoutMeasurement.height >= contentSize.height - 40) {
          onEndReached();
        }
      }
    : undefined;

  return (
    <ScrollView
      horizontal={horizontal}
      style={style}
      contentContainerStyle={contentContainerStyle}
      onScroll={handleScroll}
      {...rest}
    >
      {renderElement(ListHeaderComponent)}
      {items.length === 0
        ? renderElement(ListEmptyComponent)
        : items.map((item, index) => (
            <React.Fragment key={keyExtractor ? keyExtractor(item, index) : index}>
              {renderItem?.({ item, index })}
              {ItemSeparatorComponent && index < items.length - 1 ? <ItemSeparatorComponent /> : null}
            </React.Fragment>
          ))}
      {renderElement(ListFooterComponent)}
    </ScrollView>
  );
}

export const SectionList = FlatList as any;

export const RefreshControl = function RefreshControl(_: any) {
  return null;
};

export const StatusBar = function StatusBar(_: any) {
  return null;
};

/* -------------------------------------------------------------------------- */
/* StyleSheet                                                                   */
/* -------------------------------------------------------------------------- */

export const StyleSheet = {
  create<T extends Record<string, RNStyle>>(styles: T): T {
    return styles;
  },
  flatten: flattenStyle,
  compose: (a: StyleProp, b: StyleProp) => [a, b],
  hairlineWidth: 1,
  absoluteFill: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } as RNStyle,
  absoluteFillObject: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } as RNStyle,
};

/* -------------------------------------------------------------------------- */
/* Platform                                                                     */
/* -------------------------------------------------------------------------- */

export const Platform = {
  OS: 'web' as 'ios' | 'android' | 'web',
  Version: 0,
  isPad: false,
  isTV: false,
  select<T>(spec: { ios?: T; android?: T; native?: T; web?: T; default?: T }): T | undefined {
    if ('web' in spec) return spec.web;
    if ('default' in spec) return spec.default;
    if ('native' in spec) return spec.native;
    return spec.ios ?? spec.android;
  },
};

/* -------------------------------------------------------------------------- */
/* Dimensions / useWindowDimensions / PixelRatio                                */
/* -------------------------------------------------------------------------- */

type DimEntry = { window: any; screen: any };
const dimensionListeners = new Set<(dims: DimEntry) => void>();

function currentDimensions() {
  const window_ = {
    width: window.innerWidth,
    height: window.innerHeight,
    scale: window.devicePixelRatio || 1,
    fontScale: 1,
  };
  return { window: window_, screen: { ...window_, width: window.screen.width, height: window.screen.height } };
}

if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => {
    const dims = currentDimensions();
    dimensionListeners.forEach((l) => l(dims));
  });
}

export const Dimensions = {
  get(dim: 'window' | 'screen') {
    return currentDimensions()[dim];
  },
  addEventListener(_type: 'change', handler: (dims: DimEntry) => void) {
    dimensionListeners.add(handler);
    return { remove: () => dimensionListeners.delete(handler) };
  },
  removeEventListener(_type: 'change', handler: (dims: DimEntry) => void) {
    dimensionListeners.delete(handler);
  },
};

export function useWindowDimensions() {
  const [dims, setDims] = useState(() => currentDimensions().window);
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window: w }) => setDims(w));
    return () => sub.remove();
  }, []);
  return dims;
}

export const PixelRatio = {
  get: () => window.devicePixelRatio || 1,
  getFontScale: () => 1,
  getPixelSizeForLayoutSize: (size: number) => Math.round(size * (window.devicePixelRatio || 1)),
  roundToNearestPixel: (size: number) => Math.round(size),
};

/* -------------------------------------------------------------------------- */
/* Alert                                                                        */
/* -------------------------------------------------------------------------- */

interface AlertButton {
  text?: string;
  onPress?: (value?: string) => void;
  style?: 'default' | 'cancel' | 'destructive';
}

export const Alert = {
  alert(title: string, message?: string, buttons?: AlertButton[]) {
    const text = [title, message].filter(Boolean).join('\n\n');
    if (!buttons || buttons.length === 0) {
      window.alert(text);
      return;
    }
    if (buttons.length === 1) {
      window.alert(text);
      buttons[0].onPress?.();
      return;
    }
    // Two-or-more button dialog → use confirm. The last non-cancel button is the
    // confirm action; a `cancel` styled button is the dismiss action.
    const confirmed = window.confirm(text);
    const cancelBtn = buttons.find((b) => b.style === 'cancel');
    const confirmBtn = buttons.find((b) => b.style !== 'cancel');
    if (confirmed) confirmBtn?.onPress?.();
    else cancelBtn?.onPress?.();
  },
  prompt(title: string, message?: string, callbackOrButtons?: any) {
    const value = window.prompt([title, message].filter(Boolean).join('\n\n')) ?? '';
    if (typeof callbackOrButtons === 'function') callbackOrButtons(value);
    else if (Array.isArray(callbackOrButtons)) callbackOrButtons[callbackOrButtons.length - 1]?.onPress?.(value);
  },
};

/* -------------------------------------------------------------------------- */
/* AppState                                                                     */
/* -------------------------------------------------------------------------- */

type AppStateStatus = 'active' | 'background' | 'inactive';
type AppStateEvent = 'change' | 'focus' | 'blur' | 'memoryWarning';

class AppStateImpl {
  currentState: AppStateStatus = document.visibilityState === 'visible' ? 'active' : 'background';
  private listeners: Record<string, Set<(state: any) => void>> = {};

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.currentState = document.visibilityState === 'visible' ? 'active' : 'background';
        this.emit('change', this.currentState);
      });
      window.addEventListener('focus', () => this.emit('focus', undefined));
      window.addEventListener('blur', () => this.emit('blur', undefined));
    }
  }

  private emit(type: string, value: any) {
    this.listeners[type]?.forEach((l) => l(value));
  }

  addEventListener(type: AppStateEvent, handler: (state: any) => void) {
    (this.listeners[type] ??= new Set()).add(handler);
    return { remove: () => this.listeners[type]?.delete(handler) };
  }

  removeEventListener(type: AppStateEvent, handler: (state: any) => void) {
    this.listeners[type]?.delete(handler);
  }
}

export const AppState = new AppStateImpl();

/* -------------------------------------------------------------------------- */
/* Keyboard                                                                     */
/* -------------------------------------------------------------------------- */

export const Keyboard = {
  dismiss() {
    (document.activeElement as HTMLElement | null)?.blur?.();
  },
  addListener(_event: string, _handler: (...args: any[]) => void) {
    return { remove: () => {} };
  },
  removeAllListeners() {},
};

/* -------------------------------------------------------------------------- */
/* Linking                                                                      */
/* -------------------------------------------------------------------------- */

export const Linking = {
  async openURL(url: string) {
    window.open(url, '_blank', 'noopener');
  },
  async canOpenURL() {
    return true;
  },
  async getInitialURL() {
    return window.location.href;
  },
  addEventListener() {
    return { remove: () => {} };
  },
};

/* -------------------------------------------------------------------------- */
/* Misc                                                                         */
/* -------------------------------------------------------------------------- */

export const LogBox = {
  ignoreLogs(_: string[]) {},
  ignoreAllLogs(_?: boolean) {},
};

export const I18nManager = { isRTL: false, doLeftAndRightSwapInRTL: true };

export const InteractionManager = {
  runAfterInteractions(cb?: () => void) {
    const id = setTimeout(() => cb?.(), 0);
    return { then: (fn: () => void) => setTimeout(fn, 0), done: () => {}, cancel: () => clearTimeout(id) };
  },
  createInteractionHandle: () => 0,
  clearInteractionHandle: () => {},
};

export function useColorScheme(): 'light' | 'dark' {
  const [scheme, setScheme] = useState<'light' | 'dark'>(
    window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setScheme(e.matches ? 'dark' : 'light');
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return scheme;
}

/* -------------------------------------------------------------------------- */
/* Animated (minimal)                                                           */
/* -------------------------------------------------------------------------- */

class AnimatedValue {
  constructor(public value: number) {}
  setValue(v: number) {
    this.value = v;
  }
  interpolate() {
    return this;
  }
  addListener() {
    return '0';
  }
  removeAllListeners() {}
  stopAnimation(cb?: (v: number) => void) {
    cb?.(this.value);
  }
}

const animatedTiming = () => ({ start: (cb?: (r: { finished: boolean }) => void) => cb?.({ finished: true }) });

export const Animated = {
  View,
  Text,
  ScrollView,
  Image,
  Value: AnimatedValue,
  timing: animatedTiming,
  spring: animatedTiming,
  decay: animatedTiming,
  parallel: (anims: any[]) => ({ start: (cb?: any) => cb?.({ finished: true }) }),
  sequence: (anims: any[]) => ({ start: (cb?: any) => cb?.({ finished: true }) }),
  loop: () => ({ start: () => {}, stop: () => {} }),
  createAnimatedComponent: (Component: any) => Component,
};

export const Easing = new Proxy({}, { get: () => () => (t: number) => t });

/* -------------------------------------------------------------------------- */
/* Types re-exported for TS consumers                                           */
/* -------------------------------------------------------------------------- */

export type ViewStyle = RNStyle;
export type TextStyle = RNStyle;
export type ImageStyle = RNStyle;
export type FlexStyle = RNStyle;
export type GestureResponderEvent = any;
export type NativeSyntheticEvent<T> = { nativeEvent: T };
export type LayoutChangeEvent = { nativeEvent: { layout: { x: number; y: number; width: number; height: number } } };
export type AppStateStatus_ = AppStateStatus;
export { type AppStateStatus };

export default {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TouchableHighlight,
  TouchableWithoutFeedback,
  Pressable,
  TextInput,
  Image,
  ImageBackground,
  ActivityIndicator,
  Switch,
  SafeAreaView,
  KeyboardAvoidingView,
  FlatList,
  SectionList,
  RefreshControl,
  StatusBar,
  StyleSheet,
  Platform,
  Dimensions,
  useWindowDimensions,
  PixelRatio,
  Alert,
  AppState,
  Keyboard,
  Linking,
  LogBox,
  I18nManager,
  InteractionManager,
  Animated,
  Easing,
};
